import { useState, useMemo } from 'react';
import type { Issue } from '@/types';

interface IssueDonutChartProps {
  issues: Issue[];
}

interface StageData {
  status: string;
  count: number;
  color: string;
  lightColor: string;
  label: string;
}

const STAGE_COLORS: StageData[] = [
  { status: '0/4', count: 0, color: '#E5E5E5', lightColor: 'rgba(229, 229, 229, 0.05)', label: '0/4' },
  { status: '1/4', count: 0, color: '#93C5FD', lightColor: 'rgba(147, 197, 253, 0.05)', label: '1/4' },
  { status: '2/4', count: 0, color: '#60A5FA', lightColor: 'rgba(96, 165, 250, 0.05)', label: '2/4' },
  { status: '3/4', count: 0, color: '#2563EB', lightColor: 'rgba(37, 99, 235, 0.05)', label: '3/4' },
  { status: '4/4', count: 0, color: '#57E434', lightColor: 'rgba(87, 228, 52, 0.05)', label: '4/4' },
];

export function IssueDonutChart({ issues }: IssueDonutChartProps) {
  const [hoveredStage, setHoveredStage] = useState<string | null>(null);

  const stageData: StageData[] = useMemo(() => {
    return STAGE_COLORS.map((stage) => ({
      ...stage,
      count: issues.filter((issue) => issue.status === stage.status).length,
    }));
  }, [issues]);

  const totalCount = issues.length;
  const displayCount = hoveredStage 
    ? stageData.find(s => s.status === hoveredStage)?.count || 0
    : totalCount;
  const displayLabel = hoveredStage
    ? stageData.find(s => s.status === hoveredStage)?.label || ''
    : 'Total Issues';

  return (
    <div className="p-4 rounded-xl border border-gray-100 hover:shadow-md transition-shadow h-[300px]">
      <div className="flex items-center gap-2 mb-3">
        <div className="p-2 rounded-lg">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-4 h-4"
            style={{ color: '#6B7280' }}
          >
            <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
            <path d="M12 11h4" />
            <path d="M12 16h4" />
            <path d="M8 11h.01" />
            <path d="M8 16h.01" />
          </svg>
        </div>
        <span className="text-sm font-medium text-gray-600">Analytics</span>
      </div>

      <div className="flex items-center justify-center mb-2">
        <div className="relative w-[180px] h-[180px]">
          <svg
            width="180"
            height="180"
            viewBox="0 0 302 302"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-full"
          >
            {/* Background ring */}
            <circle
              cx="150.786"
              cy="150.786"
              r="100.5"
              stroke="rgba(91, 147, 255, 0.05)"
              strokeWidth="15"
              fill="none"
            />
            
            {/* Segments using arc paths - simplified approach with stroke-dasharray */}
            {(() => {
              const radius = 100.5;
              const circumference = 2 * Math.PI * radius;
              let cumulativePercent = 0;
              
              return stageData.map((stage) => {
                const percentage = totalCount > 0 ? stage.count / totalCount : 0;
                if (percentage === 0) return null;
                
                const strokeDasharray = circumference;
                const strokeDashoffset = circumference * (1 - percentage);
                const rotation = (cumulativePercent / 100) * 360;
                cumulativePercent += percentage * 100;
                
                return (
                  <circle
                    key={stage.status}
                    cx="150.786"
                    cy="150.786"
                    r={radius}
                    stroke={stage.color}
                    strokeWidth={hoveredStage === stage.status ? 25 : 20}
                    fill="none"
                    strokeDasharray={strokeDasharray}
                    strokeDashoffset={strokeDashoffset}
                    transform={`rotate(-90 150.786 150.786) rotate(${rotation} 150.786 150.786)`}
                    strokeLinecap="round"
                    className="cursor-pointer transition-all"
                    onMouseEnter={() => setHoveredStage(stage.status)}
                    onMouseLeave={() => setHoveredStage(null)}
                  />
                );
              });
            })()}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-3xl font-bold text-gray-800">{displayCount}</span>
            <span className="text-sm text-gray-500 mt-1">{displayLabel}</span>
          </div>
        </div>
      </div>

      <div className="flex justify-center gap-1">
        {stageData.map((stage, index) => {
          const radius = 10;
          const circumference = 2 * Math.PI * radius;
          const progress = (index + 1) / 5;
          const strokeDashoffset = circumference * (1 - progress);
          
          return (
            <div key={stage.status} className="relative w-8 h-8">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle
                  cx="16"
                  cy="16"
                  r={radius}
                  stroke={stage.lightColor}
                  strokeWidth="3"
                  fill="none"
                />
                <circle
                  cx="16"
                  cy="16"
                  r={radius}
                  stroke={stage.color}
                  strokeWidth="3"
                  fill="none"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  transform="rotate(-90 16 16)"
                  strokeLinecap="round"
                />
              </svg>
              <span 
                className="absolute inset-0 flex items-center justify-center font-semibold"
                style={{ color: stage.color, fontSize: '8px', lineHeight: '1' }}
              >
                {stage.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
